/**
 * SubscriptionClicksPanel — visitor-level subscription funnel.
 *
 * Answers the question "combien de personnes cliquent sur les boutons pour
 * s'abonner ?" at a glance:
 *
 *  ┌─────────────┐   ┌─────────────┐   ┌─────────────┐
 *  │ CTA clicks  │ → │ Checkout    │ → │ Paid        │
 *  │ (anonymous) │   │ started     │   │ conversions │
 *  └─────────────┘   └─────────────┘   └─────────────┘
 *
 * Data comes from GET /api/admin/analytics/subscription-clicks?period_days=…
 * which aggregates `cta_clicked` / `checkout_started` events and joins
 * against `payment_transactions` for the final paid step (source of truth).
 *
 * Sits in the Analytics tab above the existing DetailedFunnelPanel so
 * admins see the "who's even trying to subscribe?" signal before diving
 * into the deeper per-cohort funnel.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MousePointerClick, ShoppingCart, CheckCircle2, RotateCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [
  { value: 7,  label: '7 j' },
  { value: 30, label: '30 j' },
  { value: 90, label: '90 j' },
];

const PLAN_LABEL = {
  solo:        'Solo Mensuel (19 €/mois)',
  solo_annual: 'Solo Annuel (190 €/an)',
  unknown:     '— (plan absent)',
};

const CTA_LABEL = {
  header_register:      "Header · Commencer gratuitement",
  hero_register:        "Hero · Commencer gratuitement",
  hero_demo:            "Hero · Voir la démo",
  pricing_free:         "Pricing · Plan gratuit",
  pricing_solo_monthly: "Pricing · Solo Mensuel",
  pricing_solo_annual:  "Pricing · Solo Annuel",
  wide_map_register:    "Carte plein écran · Commencer",
  final_cta_register:   "Section finale · Commencer",
  landing_polygon_drawn:"Landing · Polygone dessiné",
  linkedin_follow:      "Footer · LinkedIn",
};

const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const fmtEur = (v) => `${Number(v || 0).toFixed(2)} €`;

export function SubscriptionClicksPanel({ token }) {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/analytics/subscription-clicks`, {
        params: { period_days: period },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Erreur de chargement des clics abonnement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period]);

  if (!data) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="subscription-clicks-panel-loading">
        <div className="text-slate-500 text-sm">Chargement des clics abonnement…</div>
      </div>
    );
  }

  const conv = data.conversion || {};
  const revenue = (data.paid_by_plan || []).reduce((s, r) => s + (r.revenue || 0), 0);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6" data-testid="subscription-clicks-panel">
      {/* Header + period selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <MousePointerClick className="w-5 h-5 text-sky-600" />
          <h2 className="text-lg font-bold text-slate-900 font-[Barlow]">Clics abonnement</h2>
          <span className="text-xs text-slate-500 hidden sm:inline">
            (visiteurs + inscrits, {data.period_days} derniers jours)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-300 bg-white" role="tablist">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 h-8 text-sm font-medium first:rounded-l-md last:rounded-r-md transition ${
                  period === p.value ? 'bg-sky-500 text-white' : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`sub-clicks-period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 h-8 rounded-md text-sm border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            data-testid="sub-clicks-refresh"
            aria-label="Rafraîchir"
          >
            <RotateCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-sky-200 bg-sky-50 rounded-lg p-4" data-testid="kpi-cta-total">
          <div className="text-xs uppercase font-semibold text-sky-700 mb-1 flex items-center gap-1">
            <MousePointerClick className="w-3 h-3" /> CTA cliqués
          </div>
          <div className="text-2xl font-bold text-sky-900">{data.cta_total}</div>
          <div className="text-xs text-sky-700 mt-1">tous boutons confondus</div>
        </div>
        <div className="border border-slate-200 rounded-lg p-4" data-testid="kpi-pricing-viewed">
          <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Page Tarifs vue</div>
          <div className="text-2xl font-bold text-slate-900">{data.pricing_viewed_total}</div>
          <div className="text-xs text-slate-500 mt-1">/subscription</div>
        </div>
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4" data-testid="kpi-checkout-total">
          <div className="text-xs uppercase font-semibold text-amber-700 mb-1 flex items-center gap-1">
            <ShoppingCart className="w-3 h-3" /> Checkout démarré
          </div>
          <div className="text-2xl font-bold text-amber-900">{data.checkout_total}</div>
          <div className="text-xs text-amber-700 mt-1">
            {data.checkout_unique_users} utilisateur{data.checkout_unique_users > 1 ? 's' : ''} unique{data.checkout_unique_users > 1 ? 's' : ''}
          </div>
        </div>
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4" data-testid="kpi-paid-total">
          <div className="text-xs uppercase font-semibold text-emerald-700 mb-1 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Paiements
          </div>
          <div className="text-2xl font-bold text-emerald-900">{data.paid_total}</div>
          <div className="text-xs text-emerald-700 mt-1">{fmtEur(revenue)} encaissés</div>
        </div>
      </div>

      {/* Conversion rates strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between" data-testid="conv-cta-to-checkout">
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">CTA → Checkout</div>
            <div className="text-xl font-bold text-slate-900">{fmtPct(conv.cta_to_checkout_pct)}</div>
          </div>
          <MousePointerClick className="w-6 h-6 text-slate-400" />
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between" data-testid="conv-pricing-to-checkout">
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">Pricing → Checkout</div>
            <div className="text-xl font-bold text-slate-900">{fmtPct(conv.pricing_to_checkout_pct)}</div>
          </div>
          <ShoppingCart className="w-6 h-6 text-slate-400" />
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between" data-testid="conv-checkout-to-paid">
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">Checkout → Paiement</div>
            <div className="text-xl font-bold text-slate-900">{fmtPct(conv.checkout_to_paid_pct)}</div>
          </div>
          <CheckCircle2 className="w-6 h-6 text-slate-400" />
        </div>
      </div>

      {/* Two side-by-side tables: checkout by plan + CTA clicks by name */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Checkout by plan */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-slate-500" />
            Clics « S&apos;abonner » par plan
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Plan</th>
                  <th className="text-right px-3 py-2 font-semibold">Clics</th>
                  <th className="text-right px-3 py-2 font-semibold">Utilisateurs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="checkout-by-plan-table">
                {data.checkout_by_plan.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-4 text-center text-slate-500">
                      Aucun clic checkout sur la période
                    </td>
                  </tr>
                ) : data.checkout_by_plan.map((row) => (
                  <tr key={row.plan} data-testid={`checkout-plan-${row.plan}`}>
                    <td className="px-3 py-2 text-slate-700">{PLAN_LABEL[row.plan] || row.plan}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">{row.clicks}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{row.unique_users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paid conversions row */}
          {data.paid_by_plan.length > 0 && (
            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Paiements par plan
              </div>
              <div className="border border-emerald-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-emerald-50 text-emerald-700 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Plan</th>
                      <th className="text-right px-3 py-2 font-semibold">Achats</th>
                      <th className="text-right px-3 py-2 font-semibold">Revenu</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-emerald-100" data-testid="paid-by-plan-table">
                    {data.paid_by_plan.map((row) => (
                      <tr key={row.plan}>
                        <td className="px-3 py-2 text-slate-700">{PLAN_LABEL[row.plan] || row.plan}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-900">{row.count}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{fmtEur(row.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* CTA clicks by name */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <MousePointerClick className="w-4 h-4 text-slate-500" />
            Clics CTAs (par bouton)
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Bouton</th>
                  <th className="text-right px-3 py-2 font-semibold">Clics</th>
                  <th className="text-right px-3 py-2 font-semibold">Sessions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="cta-clicks-table">
                {data.cta_clicks.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-4 text-center text-slate-500">
                      Aucun clic CTA sur la période
                    </td>
                  </tr>
                ) : data.cta_clicks.map((row) => (
                  <tr key={row.name} data-testid={`cta-row-${row.name}`}>
                    <td className="px-3 py-2 text-slate-700">
                      {CTA_LABEL[row.name] || <code className="text-xs bg-slate-100 px-1 rounded">{row.name}</code>}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">{row.clicks}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{row.sessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
